<script setup lang="ts">
import SourceButton from '~/components/ui/SourceButton.vue'
import SourcePopin from '~/components/ui/SourcePopin.vue'
import SourceRadio from '~/components/ui/SourceRadio.vue'
import type { ConcurrentField } from '~~/errors/ConcurrentChangeError'
import {
  fieldLabel,
  formatConflictValue,
  type ConflictChoice,
} from '~~/utils/organization/concurrentPatch'

defineProps<{
  open: boolean
  deleted: boolean
  fields: ConcurrentField[]
  choices: Record<string, ConflictChoice>
}>()

const emit = defineEmits<{
  (event: 'close' | 'confirm' | 'restore'): void
  (event: 'choose', field: string, choice: ConflictChoice): void
}>()

function onOpenChange(open: boolean): void {
  if (!open) {
    emit('close')
  }
}
</script>

<template>
  <SourcePopin
    :open="open"
    :title="deleted ? 'Fiche supprimée' : 'Modification en conflit'"
    :description="deleted
      ? 'Cette fiche a été supprimée pendant votre saisie. Le formulaire est conservé.'
      : 'Quelqu’un a modifié les mêmes champs. Choisissez la valeur à conserver, puis enregistrez à nouveau.'"
    size="medium"
    @update:open="onOpenChange"
  >
    <div v-if="!deleted" class="conflict-list">
      <section v-for="field in fields" :key="field.field" class="conflict-field">
        <p class="conflict-field__label">{{ fieldLabel(field.field) }}</p>
        <SourceRadio
          :name="`conflict-${field.field}`"
          :model-value="choices[field.field] ?? 'mine'"
          value="mine"
          :label="`Ma valeur : ${formatConflictValue(field.mine)}`"
          @update:model-value="emit('choose', field.field, 'mine')"
        />
        <SourceRadio
          :name="`conflict-${field.field}`"
          :model-value="choices[field.field] ?? 'mine'"
          value="current"
          :label="`Valeur actuelle : ${formatConflictValue(field.current)}`"
          @update:model-value="emit('choose', field.field, 'current')"
        />
      </section>
    </div>
    <template #actions>
      <SourceButton
        v-if="deleted"
        text="Ouvrir la corbeille"
        type="primary"
        @click="emit('restore')"
      />
      <SourceButton
        v-else
        text="Enregistrer ce choix"
        type="primary"
        @click="emit('confirm')"
      />
      <SourceButton text="Fermer" type="secondary" @click="emit('close')" />
    </template>
  </SourcePopin>
</template>

<style scoped lang="scss">
.conflict-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.conflict-field__label {
  margin: 0 0 0.25rem;
  font-weight: 600;
}
</style>
